import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Tooltip,
  Zoom,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  CreditCard as BankIcon,
  CheckCircle as ActiveIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  ExitToApp as TerminateIcon,
  Restore as ReinstateIcon,
} from '@mui/icons-material';
import { Employee } from '../types/employees.types';
import employeesService from '../services/employees.service';
import EmployeeDialog from '../components/employees/EmployeeDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useConfirm } from '../hooks/useConfirm';
import { toast } from 'react-toastify';
import { branchesService } from '../services/branches.service';
import { Branch } from '../types/branches.types';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const translations = {
  en: {
    title: 'Employees Management',
    subtitle: 'Manage your team effectively',
    newEmployee: 'New Employee',
    search: 'Search employees...',
    totalEmployees: 'Total Employees',
    activeEmployees: 'Active',
    newThisMonth: 'New This Month',
    normalCount: 'Normal Account',
    bDinarCount: 'B Dinar',
    withIban: 'With IBAN',
    noIban: 'No IBAN',
    employeeCode: 'Code',
    fullName: 'Full Name',
    branch: 'Branch',
    jobTitle: 'Job Title',
    phone: 'Phone',
    email: 'Email',
    hireDate: 'Hire Date',
    salary: 'Basic Salary',
    bank: 'Bank',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Are you sure you want to delete this employee?',
    noResults: 'No employees found',
    position: 'Position',
    filters: 'Filters',
    bankFileType: 'Bank Type',
    allBankTypes: 'All Types',
    normalAccount: 'Normal Account',
    bDinar: 'B Dinar',
    status: 'Status',
    allStatus: 'All',
    active: 'Active',
    inactive: 'Inactive',
    allBranches: 'All Branches',
    employeeType: 'Employee Type',
    allTypes: 'All Types',
    typeManagement: 'Management',
    typeBranch: 'Branch',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    clearFilters: 'Clear Filters',
    filteredOf: 'of',
    kwd: 'KWD',
    financialManager: 'Financial Manager',
    hrManager: 'HR Manager',
    boardChairman: 'Board Chairman',
    reportTitle: 'Employees Report',
    generatedOn: 'Generated on',
    iban: 'IBAN',
    companyFile: 'Company File',
    bankCardName: 'Bank Card Name',
  },
  ar: {
    title: 'إدارة الموظفين',
    subtitle: 'إدارة فريقك بفعالية',
    newEmployee: 'موظف جديد',
    search: 'البحث عن موظفين...',
    totalEmployees: 'إجمالي الموظفين',
    activeEmployees: 'النشطين',
    newThisMonth: 'الجدد هذا الشهر',
    normalCount: 'حساب عادي',
    bDinarCount: 'ب دينار',
    withIban: 'لديهم آيبان',
    noIban: 'بدون آيبان',
    employeeCode: 'الرمز',
    fullName: 'الاسم الكامل',
    branch: 'الفرع',
    jobTitle: 'المسمى الوظيفي',
    phone: 'الهاتف',
    email: 'البريد',
    hireDate: 'تاريخ التعيين',
    salary: 'الراتب الأساسي',
    bank: 'البنك',
    actions: 'الإجراءات',
    edit: 'تعديل',
    delete: 'حذف',
    confirmDelete: 'هل أنت متأكد من حذف هذا الموظف؟',
    noResults: 'لا يوجد موظفين',
    position: 'الوظيفة',
    filters: 'الفلاتر',
    bankFileType: 'نوع الحساب',
    allBankTypes: 'الكل',
    normalAccount: 'حساب عادي',
    bDinar: 'ب دينار',
    status: 'الحالة',
    allStatus: 'الكل',
    active: 'نشط',
    inactive: 'غير نشط',
    allBranches: 'جميع الفروع',
    employeeType: 'نوع الموظف',
    allTypes: 'الكل',
    typeManagement: 'إدارة',
    typeBranch: 'فرع',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    clearFilters: 'مسح الفلاتر',
    filteredOf: 'من',
    kwd: 'د.ك',
    financialManager: 'المدير المالي',
    hrManager: 'مدير الموارد البشرية',
    boardChairman: 'رئيس مجلس الإدارة',
    reportTitle: 'تقرير الموظفين',
    generatedOn: 'تاريخ الإنشاء',
    iban: 'رقم الآيبان',
    companyFile: 'ملف الشركة',
    bankCardName: 'اسم كارت البنك',
  },
};

const getColorFromName = (name: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
  ];
  const charCode = name.charCodeAt(0) + name.charCodeAt(name.length - 1);
  return colors[charCode % colors.length];
};

const EmployeesPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
  const canEditEmployee = (employee: Employee) => {
    return userBranchIds.length === 0 || userBranchIds.includes(employee.branchId);
  };

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBankFileType, setFilterBankFileType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterEmployeeType, setFilterEmployeeType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    loadEmployees();
    loadBranches();
    loadCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const loadEmployees = async () => {
    try {
      const response = await employeesService.getAll(includeInactive);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  // Unique employee types from data
  const employeeTypes = useMemo(() => {
    const types = new Set(employees.map(e => e.employeeType).filter(Boolean));
    return Array.from(types) as string[];
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matches =
          emp.employeeCode.toLowerCase().includes(q) ||
          emp.fullName.toLowerCase().includes(q) ||
          emp.fullNameAr.toLowerCase().includes(q) ||
          (emp.jobTitle?.toLowerCase() || '').includes(q) ||
          (emp.phone?.toLowerCase() || '').includes(q) ||
          (emp.email?.toLowerCase() || '').includes(q) ||
          (emp.bankName?.toLowerCase() || '').includes(q) ||
          (emp.bankCardName?.toLowerCase() || '').includes(q);
        if (!matches) return false;
      }
      if (filterBankFileType && emp.bankFileType !== filterBankFileType) return false;
      if (filterStatus === 'active' && !emp.isActive) return false;
      if (filterStatus === 'inactive' && emp.isActive) return false;
      if (filterBranch && String(emp.branchId) !== filterBranch) return false;
      if (filterEmployeeType && emp.employeeType !== filterEmployeeType) return false;
      return true;
    });
  }, [employees, searchQuery, filterBankFileType, filterStatus, filterBranch, filterEmployeeType]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [searchQuery, filterBankFileType, filterStatus, filterBranch, filterEmployeeType]);

  const paginatedEmployees = useMemo(
    () => filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredEmployees, page, rowsPerPage]
  );

  const stats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const newThisMonth = filteredEmployees.filter(emp => {
      const hireDate = new Date(emp.hireDate);
      return hireDate.getMonth() === currentMonth && hireDate.getFullYear() === currentYear;
    }).length;
    return {
      total: filteredEmployees.length,
      active: filteredEmployees.filter(e => e.isActive).length,
      newThisMonth,
      normalCount: filteredEmployees.filter(e => e.bankFileType === 'Regular').length,
      bDinarCount: filteredEmployees.filter(e => e.bankFileType === 'Worker').length,
      withIban: filteredEmployees.filter(e => e.iban && e.iban.trim() !== '').length,
      noIban: filteredEmployees.filter(e => !e.iban || e.iban.trim() === '').length,
    };
  }, [filteredEmployees]);

  const hasActiveFilters =
    searchQuery || filterBankFileType || filterStatus || filterBranch || filterEmployeeType;

  const clearFilters = () => {
    setSearchQuery('');
    setFilterBankFileType('');
    setFilterStatus('');
    setFilterBranch('');
    setFilterEmployeeType('');
  };

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await employeesService.delete(id);
      loadEmployees();
    } catch (error) {
      toast.error(isRTL ? 'فشل حذف الموظف' : 'Failed to delete employee');
    }
  };

  // ─── Termination dialog state ─────────────────────────────────────
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateEmployee, setTerminateEmployee] = useState<Employee | null>(null);
  const [terminateDate, setTerminateDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [terminateType, setTerminateType] = useState<string>('Resigned');
  const [terminateReason, setTerminateReason] = useState<string>('');
  const [terminateBusy, setTerminateBusy] = useState(false);

  const openTerminateDialog = (emp: Employee) => {
    setTerminateEmployee(emp);
    setTerminateDate(new Date().toISOString().split('T')[0]);
    setTerminateType('Resigned');
    setTerminateReason('');
    setTerminateOpen(true);
  };

  const submitTerminate = async () => {
    if (!terminateEmployee) return;
    if (!terminateDate) {
      toast.error(isRTL ? 'الرجاء اختيار تاريخ إنهاء الخدمة' : 'Please select a termination date');
      return;
    }
    setTerminateBusy(true);
    try {
      await employeesService.terminate(terminateEmployee.employeeId, {
        terminationDate: terminateDate,
        terminationType: terminateType,
        reason: terminateReason.trim() || undefined,
      });
      toast.success(isRTL
        ? `تم إنهاء خدمات ${terminateEmployee.fullName}. لن يظهر في الرواتب أو ملف البنك أو التايم شيت.`
        : `${terminateEmployee.fullName} terminated. Will be excluded from payroll, bank file, and timesheet.`);
      setTerminateOpen(false);
      await loadEmployees();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل إنهاء الخدمة' : 'Termination failed'));
    } finally {
      setTerminateBusy(false);
    }
  };

  const handleReinstate = async (emp: Employee) => {
    const ok = await confirm({
      message: isRTL
        ? `إعادة تفعيل ${emp.fullName}؟ سيعود للظهور في الرواتب والتايم شيت.`
        : `Reinstate ${emp.fullName}? Will reappear in payroll and timesheet.`,
      severity: 'info',
    });
    if (!ok) return;
    try {
      await employeesService.reinstate(emp.employeeId);
      toast.success(isRTL ? 'تم إعادة التفعيل' : 'Reinstated');
      await loadEmployees();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل إعادة التفعيل' : 'Reinstate failed'));
    }
  };

  const handleCloseDialog = () => {
    setSelectedEmployee(null);
    setDialogOpen(false);
  };

  const getBranchName = (branchId?: number): string => {
    if (!branchId) return '—';
    const branch = branches.find(b => b.branchId === branchId);
    if (!branch) return '—';
    return isRTL ? branch.branchNameAr : branch.branchName;
  };

  // ─── Print List ─────────────────────────────────────────────────────────
  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredEmployees.map(emp => `
      <tr>
        <td>${emp.employeeCode}</td>
        <td>${isRTL ? emp.fullNameAr : emp.fullName}</td>
        <td>${emp.jobTitle || '-'}</td>
        <td>${getBranchName(emp.branchId)}</td>
        <td>${emp.phone || '-'}</td>
        <td>${emp.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        .header{text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;}
        .header h2{color:#1976d2;margin:5px 0;}
        .header p{font-size:11px;color:#666;margin:0;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${isRTL ? 'قائمة الموظفين' : 'Employees List'}</h1>
      <table>
        <thead><tr>
          <th>${t.employeeCode}</th>
          <th>${t.fullName}</th>
          <th>${t.jobTitle}</th>
          <th>${t.branch}</th>
          <th>${t.phone}</th>
          <th>${isRTL ? 'الحالة' : 'Status'}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  // ─── Excel Export ────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.reportTitle);

      let currentRow = 1;

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('B1:L1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FF5E35B1' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:L2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:L3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Report Title
      worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = t.reportTitle;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      worksheet.getRow(currentRow).height = 30;
      currentRow++;

      // Filter info row
      worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
      const filterCell = worksheet.getCell(`A${currentRow}`);
      const filterInfo = [
        filterBankFileType ? `${t.bankFileType}: ${filterBankFileType === 'Regular' ? t.normalAccount : t.bDinar}` : '',
        filterStatus ? `${t.status}: ${filterStatus === 'active' ? t.active : t.inactive}` : '',
        filterBranch ? `${t.branch}: ${getBranchName(Number(filterBranch))}` : '',
        filterEmployeeType ? `${t.employeeType}: ${filterEmployeeType}` : '',
      ].filter(Boolean).join(' | ');
      filterCell.value = filterInfo
        ? `${t.generatedOn}: ${new Date().toLocaleDateString('en-GB')} | ${filterInfo}`
        : `${t.generatedOn}: ${new Date().toLocaleDateString('en-GB')} | ${t.totalEmployees}: ${filteredEmployees.length}`;
      filterCell.font = { size: 10, color: { argb: 'FF5E35B1' } };
      filterCell.alignment = { horizontal: 'center' };
      currentRow += 2;

      // Summary stats
      const summaryLabels = [
        { label: t.totalEmployees, value: String(filteredEmployees.length), color: 'FF5E35B1' },
        { label: t.active, value: String(filteredEmployees.filter(e => e.isActive).length), color: 'FF2E7D32' },
        { label: t.normalAccount, value: String(filteredEmployees.filter(e => e.bankFileType === 'Regular').length), color: 'FF1565C0' },
        { label: t.bDinar, value: String(filteredEmployees.filter(e => e.bankFileType === 'Worker').length), color: 'FF6A1B9A' },
      ];
      summaryLabels.forEach((s, idx) => {
        const col = String.fromCharCode('A'.charCodeAt(0) + idx * 3);
        const endCol = String.fromCharCode(col.charCodeAt(0) + 2);
        worksheet.mergeCells(`${col}${currentRow}:${endCol}${currentRow}`);
        const cell = worksheet.getCell(`${col}${currentRow}`);
        cell.value = `${s.label}: ${s.value}`;
        cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: s.color } };
      });
      currentRow += 2;

      // Headers
      const headers = [
        t.employeeCode, t.fullName, t.fullName + ' (AR)', t.branch,
        t.jobTitle, t.salary, t.bankFileType, t.bank,
        t.iban, t.companyFile, t.bankCardName, t.hireDate,
      ];
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;
      currentRow++;

      // Data rows
      filteredEmployees.forEach((emp, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          emp.employeeCode,
          emp.fullName,
          emp.fullNameAr,
          getBranchName(emp.branchId),
          emp.jobTitle || '',
          emp.basicSalary || 0,
          emp.bankFileType === 'Regular' ? (isRTL ? 'حساب عادي' : 'Normal Account')
            : emp.bankFileType === 'Worker' ? (isRTL ? 'ب دينار' : 'B Dinar') : (emp.bankFileType || ''),
          emp.bankName || '',
          emp.iban || '',
          emp.companyFileNumber || '',
          emp.bankCardName || '',
          new Date(emp.hireDate).toLocaleDateString('en-GB'),
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        row.getCell(6).numFmt = '#,##0.000';
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF8FF' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 12;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 30;
      worksheet.getColumn(4).width = 18;
      worksheet.getColumn(5).width = 18;
      worksheet.getColumn(6).width = 12;
      worksheet.getColumn(7).width = 14;
      worksheet.getColumn(8).width = 22;
      worksheet.getColumn(9).width = 36;
      worksheet.getColumn(10).width = 20;
      worksheet.getColumn(11).width = 28;
      worksheet.getColumn(12).width = 14;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Employees_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل تصدير Excel' : 'Excel export failed');
    }
  };

  // ─── PDF Export ──────────────────────────────────────────────────────────
  const exportToPdf = async () => {
    toast.info(isRTL ? 'جاري إنشاء PDF...' : 'Generating PDF...');

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '297mm';
    tempDiv.style.background = 'white';
    tempDiv.style.padding = '10mm';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.direction = isRTL ? 'rtl' : 'ltr';

    const activeCount = filteredEmployees.filter(e => e.isActive).length;
    const normalCount = filteredEmployees.filter(e => e.bankFileType === 'Regular').length;
    const bDinarCount = filteredEmployees.filter(e => e.bankFileType === 'Worker').length;

    tempDiv.innerHTML = `
      <div style="text-align:center;border-bottom:3px solid #5E35B1;padding-bottom:10px;margin-bottom:12px;">
        ${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}" style="width:70px;height:55px;margin-bottom:8px;" alt="Logo">` : ''}
        <div style="font-size:22px;font-weight:bold;color:#3700B3;">${isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</div>
        <div style="font-size:10px;color:#666;margin-top:2px;">${companyInfo?.address || ''}</div>
        <div style="font-size:10px;color:#666;">${companyInfo?.phone || ''} | ${companyInfo?.email || ''}</div>
      </div>

      <div style="background:#5E35B1;color:white;padding:8px;text-align:center;font-size:16px;font-weight:bold;margin:10px 0;">
        ${t.reportTitle}
      </div>
      <div style="text-align:center;margin-bottom:10px;font-size:10px;color:#666;">
        ${t.generatedOn}: ${new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
        ${filterBankFileType ? ` | ${t.bankFileType}: ${filterBankFileType === 'Regular' ? t.normalAccount : t.bDinar}` : ''}
        ${filterStatus ? ` | ${t.status}: ${filterStatus === 'active' ? t.active : t.inactive}` : ''}
      </div>

      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:10px 0;">
        <div style="border:2px solid #5E35B1;padding:8px;text-align:center;border-radius:5px;">
          <div style="font-size:10px;color:#666;margin-bottom:3px;">${t.totalEmployees}</div>
          <div style="font-size:18px;font-weight:bold;color:#5E35B1;">${filteredEmployees.length}</div>
        </div>
        <div style="border:2px solid #2E7D32;padding:8px;text-align:center;border-radius:5px;">
          <div style="font-size:10px;color:#666;margin-bottom:3px;">${t.active}</div>
          <div style="font-size:18px;font-weight:bold;color:#2E7D32;">${activeCount}</div>
        </div>
        <div style="border:2px solid #1565C0;padding:8px;text-align:center;border-radius:5px;">
          <div style="font-size:10px;color:#666;margin-bottom:3px;">${t.normalAccount}</div>
          <div style="font-size:18px;font-weight:bold;color:#1565C0;">${normalCount}</div>
        </div>
        <div style="border:2px solid #6A1B9A;padding:8px;text-align:center;border-radius:5px;">
          <div style="font-size:10px;color:#666;margin-bottom:3px;">${t.bDinar}</div>
          <div style="font-size:18px;font-weight:bold;color:#6A1B9A;">${bDinarCount}</div>
        </div>
      </div>

      <table style="width:100%;border-collapse:collapse;margin:10px 0;font-size:9px;">
        <thead>
          <tr style="background:#5E35B1;color:white;">
            <th style="padding:6px 4px;text-align:${isRTL ? 'right' : 'left'};">${t.employeeCode}</th>
            <th style="padding:6px 4px;text-align:${isRTL ? 'right' : 'left'};">${t.fullName}</th>
            <th style="padding:6px 4px;text-align:${isRTL ? 'right' : 'left'};">${t.branch}</th>
            <th style="padding:6px 4px;text-align:${isRTL ? 'right' : 'left'};">${t.jobTitle}</th>
            <th style="padding:6px 4px;text-align:center;">${t.salary}</th>
            <th style="padding:6px 4px;text-align:center;">${t.bankFileType}</th>
            <th style="padding:6px 4px;text-align:${isRTL ? 'right' : 'left'};">${t.bank}</th>
            <th style="padding:6px 4px;text-align:${isRTL ? 'right' : 'left'};">${t.hireDate}</th>
          </tr>
        </thead>
        <tbody>
          ${filteredEmployees.map((emp, idx) => `
            <tr style="background:${idx % 2 === 0 ? '#FFFFFF' : '#F3E5F5'};">
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;color:#000;">${emp.employeeCode}</td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;color:#000;">${isRTL ? emp.fullNameAr : emp.fullName}</td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;color:#000;">${getBranchName(emp.branchId)}</td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;color:#000;">${emp.jobTitle || '—'}</td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;text-align:center;color:#000;">${(emp.basicSalary || 0).toFixed(3)}</td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;text-align:center;">
                <span style="background:${emp.bankFileType === 'Regular' ? '#1565C0' : '#6A1B9A'};color:white;padding:2px 6px;border-radius:10px;font-size:8px;">
                  ${emp.bankFileType === 'Regular' ? (isRTL ? 'عادي' : 'Normal') : emp.bankFileType === 'Worker' ? (isRTL ? 'ب دينار' : 'B Dinar') : (emp.bankFileType || '—')}
                </span>
              </td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;color:#000;">${emp.bankName || '—'}</td>
              <td style="padding:5px 4px;border-bottom:1px solid #E0E0E0;color:#000;">${new Date(emp.hireDate).toLocaleDateString('en-GB')}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-top:40px;">
        <div style="text-align:center;padding:10px;border-top:2px solid #333;">
          <div style="height:40px;"></div>
          <div style="font-weight:bold;margin-top:5px;color:#5E35B1;font-size:11px;">${t.financialManager}</div>
        </div>
        <div style="text-align:center;padding:10px;border-top:2px solid #333;">
          <div style="height:40px;"></div>
          <div style="font-weight:bold;margin-top:5px;color:#5E35B1;font-size:11px;">${t.hrManager}</div>
        </div>
        <div style="text-align:center;padding:10px;border-top:2px solid #333;">
          <div style="height:40px;"></div>
          <div style="font-weight:bold;margin-top:5px;color:#5E35B1;font-size:11px;">${t.boardChairman}</div>
        </div>
      </div>
    `;

    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape for more columns
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Employees_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(t.exportPdf + ' ✓');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* ── Header ───────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4527A0 0%, #7B1FA2 50%, #9C27B0 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(94,53,177,0.35)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t.subtitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadEmployees} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'طباعة القائمة' : 'Print List'}>
              <IconButton
                onClick={handlePrintList}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.exportExcel}>
              <IconButton
                onClick={exportToExcel}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <ExcelIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.exportPdf}>
              <IconButton
                onClick={exportToPdf}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <PdfIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{
                bgcolor: 'white',
                color: '#5E35B1',
                fontWeight: 'bold',
                px: 3,
                py: 1.5,
                ml: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.3s',
              }}
            >
              {t.newEmployee}
            </Button>
          </Box>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[
            {
              icon: <GroupsIcon sx={{ fontSize: 26 }} />,
              value: stats.total,
              label: t.totalEmployees,
              iconBg: 'rgba(255,255,255,0.25)',
              filterKey: null,
              filterVal: null,
            },
            {
              icon: <ActiveIcon sx={{ fontSize: 26 }} />,
              value: stats.active,
              label: t.activeEmployees,
              iconBg: 'rgba(76,175,80,0.35)',
              filterKey: 'status',
              filterVal: 'active',
            },
            {
              icon: <TrendingUpIcon sx={{ fontSize: 26 }} />,
              value: stats.newThisMonth,
              label: t.newThisMonth,
              iconBg: 'rgba(255,193,7,0.35)',
              filterKey: null,
              filterVal: null,
            },
            {
              icon: <BankIcon sx={{ fontSize: 26 }} />,
              value: stats.normalCount,
              label: t.normalCount,
              iconBg: 'rgba(33,150,243,0.35)',
              filterKey: 'bankFileType',
              filterVal: 'Regular',
            },
            {
              icon: <BankIcon sx={{ fontSize: 26 }} />,
              value: stats.bDinarCount,
              label: t.bDinarCount,
              iconBg: 'rgba(156,39,176,0.35)',
              filterKey: 'bankFileType',
              filterVal: 'Worker',
            },
            {
              icon: <ActiveIcon sx={{ fontSize: 26 }} />,
              value: stats.withIban,
              label: t.withIban,
              iconBg: 'rgba(0,200,150,0.35)',
              filterKey: null,
              filterVal: null,
            },
            {
              icon: <ClearIcon sx={{ fontSize: 26 }} />,
              value: stats.noIban,
              label: t.noIban,
              iconBg: 'rgba(244,67,54,0.35)',
              filterKey: null,
              filterVal: null,
            },
          ].map((stat, i) => {
            const isActive = stat.filterKey === 'bankFileType'
              ? filterBankFileType === stat.filterVal
              : stat.filterKey === 'status'
              ? filterStatus === stat.filterVal
              : false;
            return (
              <Box
                key={i}
                onClick={() => {
                  if (!stat.filterKey) return;
                  if (stat.filterKey === 'bankFileType') {
                    setFilterBankFileType(isActive ? '' : stat.filterVal!);
                  } else if (stat.filterKey === 'status') {
                    setFilterStatus(isActive ? '' : stat.filterVal!);
                  }
                }}
                sx={{
                  flex: 1, minWidth: 130,
                  bgcolor: isActive ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(10px)',
                  p: 2, borderRadius: 2,
                  border: isActive ? '2px solid rgba(255,255,255,0.8)' : '1px solid rgba(255,255,255,0.2)',
                  cursor: stat.filterKey ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  '&:hover': stat.filterKey ? {
                    bgcolor: 'rgba(255,255,255,0.25)',
                    transform: 'translateY(-2px)',
                  } : {},
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ bgcolor: stat.iconBg, p: 1, borderRadius: 1.5, display: 'flex', flexShrink: 0 }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" lineHeight={1}>{stat.value}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.72rem' }}>{stat.label}</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Filters ───────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">{t.filters}</Typography>
          {hasActiveFilters && (
            <Chip
              label={`${filteredEmployees.length} ${t.filteredOf} ${employees.length}`}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ ml: 'auto', color: 'text.secondary' }}
            >
              {t.clearFilters}
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* Search */}
          <Box sx={{ flex: '1 1 280px', minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>

          {/* Bank File Type */}
          <Box sx={{ flex: '0 1 170px', minWidth: 140 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t.bankFileType}</InputLabel>
              <Select
                value={filterBankFileType}
                label={t.bankFileType}
                onChange={(e) => setFilterBankFileType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">{t.allBankTypes}</MenuItem>
                <MenuItem value="Regular">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1565C0' }} />
                    {t.normalAccount}
                  </Box>
                </MenuItem>
                <MenuItem value="Worker">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6A1B9A' }} />
                    {t.bDinar}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Status */}
          <Box sx={{ flex: '0 1 150px', minWidth: 120 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t.status}</InputLabel>
              <Select
                value={filterStatus}
                label={t.status}
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">{t.allStatus}</MenuItem>
                <MenuItem value="active">{t.active}</MenuItem>
                <MenuItem value="inactive">{t.inactive}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Include terminated toggle — needed to see/reinstate terminated staff */}
          <Box sx={{ flex: '0 1 auto', display: 'flex', alignItems: 'center' }}>
            <Button
              variant={includeInactive ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setIncludeInactive(v => !v)}
              sx={{
                borderRadius: 2,
                bgcolor: includeInactive ? '#e65100' : undefined,
                color: includeInactive ? 'white' : undefined,
                '&:hover': { bgcolor: includeInactive ? '#bf360c' : undefined },
              }}
            >
              {includeInactive
                ? (isRTL ? 'إخفاء المُنهية خدماتهم' : 'Hide Terminated')
                : (isRTL ? 'عرض المُنهية خدماتهم' : 'Show Terminated')}
            </Button>
          </Box>

          {/* Branch */}
          <Box sx={{ flex: '0 1 200px', minWidth: 150 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t.branch}</InputLabel>
              <Select
                value={filterBranch}
                label={t.branch}
                onChange={(e) => setFilterBranch(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">{t.allBranches}</MenuItem>
                {branches.map(b => (
                  <MenuItem key={b.branchId} value={String(b.branchId)}>
                    {isRTL ? b.branchNameAr : b.branchName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Employee Type */}
          <Box sx={{ flex: '0 1 170px', minWidth: 140 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t.employeeType}</InputLabel>
              <Select
                value={filterEmployeeType}
                label={t.employeeType}
                onChange={(e) => setFilterEmployeeType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">{t.allTypes}</MenuItem>
                {employeeTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type === 'Management' ? t.typeManagement
                      : type === 'Branch' ? t.typeBranch
                      : type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* ── Employee Cards ────────────────────────────────── */}
      {filteredEmployees.length === 0 ? (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 2 }}>
          <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t.noResults}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {paginatedEmployees.map((employee, index) => (
            <Zoom in={true} key={employee.employeeId} style={{ transitionDelay: `${Math.min(index, 10) * 20}ms` }}>
              <Paper
                sx={{
                  p: 3, borderRadius: 2, transition: 'all 0.3s',
                  border: '1px solid', borderColor: 'divider',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      width: 70, height: 70,
                      bgcolor: getColorFromName(employee.fullName),
                      fontSize: '1.8rem', fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {(isRTL ? employee.fullNameAr : employee.fullName).charAt(0).toUpperCase()}
                  </Avatar>

                  {/* Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" fontWeight="bold">
                        {isRTL ? employee.fullNameAr : employee.fullName}
                      </Typography>
                      <Chip label={employee.employeeCode} size="small" sx={{ bgcolor: '#5E35B1', color: 'white', fontWeight: 'bold' }} />
                      {employee.isActive && (
                        <Chip label={t.active} size="small" color="success" sx={{ fontWeight: 'bold' }} />
                      )}
                      {employee.bankFileType && (
                        <Chip
                          icon={<BankIcon sx={{ fontSize: '14px !important' }} />}
                          label={employee.bankFileType === 'Regular' ? t.normalAccount : t.bDinar}
                          size="small"
                          sx={{
                            bgcolor: employee.bankFileType === 'Regular' ? '#1565C0' : '#6A1B9A',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                      {employee.jobTitle && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">{employee.jobTitle}</Typography>
                        </Box>
                      )}
                      {employee.branchId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="body2" color="primary">{getBranchName(employee.branchId)}</Typography>
                        </Box>
                      )}
                      {employee.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">{employee.phone}</Typography>
                        </Box>
                      )}
                      {employee.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">{employee.email}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(employee.hireDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
                        </Typography>
                      </Box>
                      {employee.bankName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BankIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">{employee.bankName}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Actions */}
                  {canEditEmployee(employee) && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t.edit}>
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(employee)}
                          sx={{
                            bgcolor: 'primary.main', color: 'white',
                            '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.1)' },
                            transition: 'all 0.2s',
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {employee.isActive ? (
                        <Tooltip title={isRTL ? 'إنهاء خدمات' : 'Terminate'}>
                          <IconButton
                            onClick={() => openTerminateDialog(employee)}
                            sx={{
                              bgcolor: '#e65100', color: 'white',
                              '&:hover': { bgcolor: '#bf360c', transform: 'scale(1.1)' },
                              transition: 'all 0.2s',
                            }}
                          >
                            <TerminateIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={isRTL ? 'إعادة تفعيل' : 'Reinstate'}>
                          <IconButton
                            onClick={() => handleReinstate(employee)}
                            sx={{
                              bgcolor: '#2e7d32', color: 'white',
                              '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
                              transition: 'all 0.2s',
                            }}
                          >
                            <ReinstateIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t.delete}>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(employee.employeeId)}
                          sx={{
                            bgcolor: 'error.main', color: 'white',
                            '&:hover': { bgcolor: 'error.dark', transform: 'scale(1.1)' },
                            transition: 'all 0.2s',
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Zoom>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <TablePagination
              component="div"
              count={filteredEmployees.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </Box>
        </Box>
      )}

      <EmployeeDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={loadEmployees}
        employee={selectedEmployee}
      />

      {/* Termination Dialog */}
      <Dialog open={terminateOpen} onClose={() => !terminateBusy && setTerminateOpen(false)} maxWidth="sm" fullWidth
        dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: '#e65100', color: 'white', fontWeight: 700 }}>
          {isRTL ? 'إنهاء خدمة موظف' : 'Terminate Employee'} — {terminateEmployee?.fullName}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isRTL
                ? 'بعد الحفظ: الموظف لن يظهر في الرواتب الجديدة، ولا في ملفات البنك، ولا في التايم شيت ابتداءً من تاريخ إنهاء الخدمة.'
                : 'After saving: employee will be excluded from new payrolls, bank export files, and timesheet entries from the termination date onward.'}
            </Typography>
            <TextField
              label={isRTL ? 'تاريخ إنهاء الخدمة' : 'Termination Date'}
              type="date" fullWidth size="small"
              value={terminateDate}
              onChange={(e) => setTerminateDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{isRTL ? 'نوع إنهاء الخدمة' : 'Termination Type'}</InputLabel>
              <Select value={terminateType} label={isRTL ? 'نوع إنهاء الخدمة' : 'Termination Type'}
                onChange={(e) => setTerminateType(e.target.value)}>
                <MenuItem value="Resigned">{isRTL ? 'استقالة' : 'Resigned'}</MenuItem>
                <MenuItem value="Terminated">{isRTL ? 'إقالة' : 'Terminated'}</MenuItem>
                <MenuItem value="ContractEnded">{isRTL ? 'انتهاء عقد' : 'Contract Ended'}</MenuItem>
                <MenuItem value="Retired">{isRTL ? 'تقاعد' : 'Retired'}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={isRTL ? 'السبب (اختياري)' : 'Reason (optional)'}
              multiline rows={3} fullWidth size="small"
              value={terminateReason}
              onChange={(e) => setTerminateReason(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTerminateOpen(false)} disabled={terminateBusy}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button variant="contained" onClick={submitTerminate} disabled={terminateBusy}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
            {terminateBusy ? (isRTL ? 'جاري...' : 'Working...') : (isRTL ? 'تأكيد إنهاء الخدمة' : 'Confirm Termination')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPage;
